import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Envelope, Trash, Phone, ArrowSquareOut, Check, Eye } from "@phosphor-icons/react";
import { PUBLIC_ORIGIN } from "../lib/domain";

const STATUS_LABELS = {
  nouveau: { label: "NOUVEAU", color: "bg-[#D90429] text-white" },
  lu: { label: "LU", color: "bg-secondary text-foreground" },
  traite: { label: "TRAITÉ", color: "bg-foreground text-background" },
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
}

export default function ContactAdmin() {
  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [openMsg, setOpenMsg] = useState(null);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);

  const sendReply = async () => {
    if (!replyText.trim()) { toast.error("La réponse est vide"); return; }
    setSendingReply(true);
    try {
      await api.post(`/contact-messages/${openMsg.id}/reply`, { body: replyText });
      toast.success("Réponse envoyée ✓");
      setReplyText("");
      setOpenMsg({ ...openMsg, status: "traite" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'envoi"); }
    finally { setSendingReply(false); }
  };

  const load = () => {
    setLoading(true);
    api.get("/contact-messages")
      .then(r => setMessages(r.data || []))
      .catch(() => toast.error("Échec du chargement"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const sources = Array.from(new Set(messages.map(m => m.source || "autre")));
  const SOURCE_LABELS = { accueil: "Accueil", academie: "Académie", circuit: "Circuit", tarifs: "Tarifs", horaire: "Horaire", petits_samourais: "Petits Samouraïs", autre: "Autre" };
  const srcLabel = (s) => SOURCE_LABELS[s] || (s || "autre");
  const filtered = messages.filter(m =>
    (filter === "all" || m.status === filter) &&
    (sourceFilter === "all" || (m.source || "autre") === sourceFilter));

  const setStatus = async (msg, status) => {
    try {
      const r = await api.patch(`/contact-messages/${msg.id}`, { status });
      setMessages(ms => ms.map(m => m.id === msg.id ? r.data : m));
      if (openMsg?.id === msg.id) setOpenMsg(r.data);
      toast.success(`Marqué comme ${STATUS_LABELS[status].label.toLowerCase()}`);
    } catch {
      toast.error("Échec de la mise à jour");
    }
  };

  const remove = async (msg) => {
    if (!window.confirm(`Supprimer le message de ${msg.name} ?`)) return;
    try {
      await api.delete(`/contact-messages/${msg.id}`);
      setMessages(ms => ms.filter(m => m.id !== msg.id));
      if (openMsg?.id === msg.id) setOpenMsg(null);
      toast.success("Message supprimé");
    } catch {
      toast.error("Échec de la suppression");
    }
  };

  const open = (msg) => {
    setOpenMsg(msg);
    if (msg.status === "nouveau") setStatus(msg, "lu");
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="mb-8">
        <div className="ticker text-[#D90429] mb-2">CONTACT</div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Messages reçus</h1>
        <p className="text-muted-foreground text-sm mt-2">Tous les messages soumis depuis les vitrines publiques.</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { id: "all", label: "Tous" },
          { id: "nouveau", label: "Nouveaux" },
          { id: "lu", label: "Lus" },
          { id: "traite", label: "Traités" },
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`contact-filter-${f.id}`}
            className={`px-4 py-2 ticker hard-border ${filter === f.id ? "bg-foreground text-background" : "hover:bg-secondary"}`}
          >
            {f.label} ({f.id === "all" ? messages.length : messages.filter(m => m.status === f.id).length})
          </button>
        ))}
      </div>

      {/* Filtre par source (vitrine d'origine) */}
      <div className="flex flex-wrap gap-2 mb-6 items-center" data-testid="contact-source-filters">
        <span className="ticker text-[10px] text-muted-foreground uppercase mr-1">Source :</span>
        {["all", ...sources].map(s => (
          <button
            key={s}
            onClick={() => setSourceFilter(s)}
            data-testid={`contact-source-${s}`}
            className={`px-3 py-1.5 ticker text-xs hard-border ${sourceFilter === s ? "bg-[#D90429] text-white" : "hover:bg-secondary"}`}
          >
            {s === "all" ? "Toutes" : srcLabel(s)} ({s === "all" ? messages.length : messages.filter(m => (m.source || "autre") === s).length})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-muted-foreground ticker">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="hard-border p-12 text-center" data-testid="contact-empty">
          <Envelope size={40} className="mx-auto text-muted-foreground mb-4" />
          <div className="text-xl font-bold mb-2">Aucun message</div>
          <div className="text-muted-foreground text-sm">Les messages soumis depuis le site public apparaîtront ici.</div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-6">
          <div className="hard-border" data-testid="contact-list">
            {filtered.map(m => (
              <button
                key={m.id}
                onClick={() => open(m)}
                data-testid={`contact-row-${m.id}`}
                className={`w-full text-left px-5 py-4 hard-border-b last:border-b-0 transition-colors ${openMsg?.id === m.id ? "bg-secondary" : "hover:bg-secondary/50"}`}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className={`ticker text-[10px] px-2 py-0.5 ${STATUS_LABELS[m.status].color}`}>
                    {STATUS_LABELS[m.status].label}
                  </span>
                  <span className="ticker text-[10px] px-2 py-0.5 bg-secondary text-foreground/70" data-testid={`contact-source-tag-${m.id}`}>{srcLabel(m.source || "autre")}</span>
                </div>
                <div className="font-semibold">{m.name}</div>
                <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                <div className="text-sm text-muted-foreground mt-1 line-clamp-2">{m.message}</div>
              </button>
            ))}
          </div>

          <div className="hard-border p-6" data-testid="contact-detail">
            {!openMsg ? (
              <div className="text-center text-muted-foreground py-12">
                <Envelope size={32} className="mx-auto mb-3" />
                <div className="ticker">SÉLECTIONNEZ UN MESSAGE</div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <span className={`ticker text-[10px] px-2 py-1 ${STATUS_LABELS[openMsg.status].color}`}>
                    {STATUS_LABELS[openMsg.status].label}
                  </span>
                  <span className="text-xs text-muted-foreground">{formatDate(openMsg.created_at)} · source: {openMsg.source}</span>
                </div>
                <div className="text-2xl font-bold mb-1">{openMsg.name}</div>
                <div className="flex flex-wrap gap-3 text-sm text-muted-foreground mb-4">
                  <a href={`mailto:${openMsg.email}`} className="flex items-center gap-1 hover:text-[#D90429]" data-testid="contact-detail-email">
                    <Envelope size={14} /> {openMsg.email}
                  </a>
                  {openMsg.phone && (
                    <a href={`tel:${openMsg.phone}`} className="flex items-center gap-1 hover:text-[#D90429]">
                      <Phone size={14} /> {openMsg.phone}
                    </a>
                  )}
                </div>
                <div className="hard-border p-4 bg-secondary/30 mb-6 whitespace-pre-wrap text-sm leading-relaxed">{openMsg.message}</div>

                {/* Répondre directement depuis la boîte (sans quitter l'app) */}
                <div className="hard-border p-4 mb-6" data-testid="contact-reply-box">
                  <div className="ticker text-[10px] uppercase text-muted-foreground mb-2">Répondre à {openMsg.name}</div>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    rows={4}
                    placeholder="Écris ta réponse ici… (envoyée par courriel)"
                    data-testid="contact-reply-textarea"
                    className="w-full border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:border-[#D90429] resize-y"
                  />
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="reply-templates">
                    {[
                      { k: "horaire", label: "Horaire", txt: `Merci pour ton message ! Voici notre horaire à jour : ${PUBLIC_ORIGIN}/horaire — n'hésite pas si tu veux réserver un cours d'essai (19,98 $).` },
                      { k: "tarifs", label: "Tarifs", txt: `Merci de ton intérêt ! Tu trouveras tous nos forfaits ici : ${PUBLIC_ORIGIN}/tarifs — je reste disponible pour t'aider à choisir celui qui te convient.` },
                      { k: "essai", label: "cours d'essai (19,98 $)", txt: "Avec plaisir ! On t'offre un cours d'essai (19,98 $) sans engagement. Dis-moi tes disponibilités et je te réserve une place." },
                    ].map((t) => (
                      <button key={t.k} type="button" onClick={() => setReplyText(t.txt)} data-testid={`reply-tpl-${t.k}`}
                        className="ticker text-[10px] px-2.5 py-1 hard-border hover:bg-secondary">+ {t.label}</button>
                    ))}
                  </div>
                  <button onClick={sendReply} disabled={sendingReply} data-testid="contact-reply-send"
                    className="mt-2 hard-border px-4 py-2 ticker bg-[#D90429] text-white border-[#D90429] hover:bg-black hover:border-black flex items-center gap-2 disabled:opacity-60">
                    <Envelope size={14} /> {sendingReply ? "Envoi…" : "Envoyer la réponse"}
                  </button>
                </div>

                <div className="flex flex-wrap gap-2">
                  <a
                    href={`mailto:${openMsg.email}?subject=${encodeURIComponent(`Re: votre message du ${formatDate(openMsg.created_at)}`)}`}
                    target="_blank" rel="noreferrer"
                    data-testid="contact-reply"
                    className="hard-border px-4 py-2 ticker bg-[#D90429] text-white border-[#D90429] hover:bg-black hover:border-black flex items-center gap-2"
                  >
                    <ArrowSquareOut size={14} /> Répondre par courriel
                  </a>
                  {openMsg.status !== "lu" && (
                    <button onClick={() => setStatus(openMsg, "lu")} data-testid="contact-mark-lu" className="hard-border px-4 py-2 ticker hover:bg-secondary flex items-center gap-2">
                      <Eye size={14} /> Marquer lu
                    </button>
                  )}
                  {openMsg.status !== "traite" && (
                    <button onClick={() => setStatus(openMsg, "traite")} data-testid="contact-mark-traite" className="hard-border px-4 py-2 ticker hover:bg-secondary flex items-center gap-2">
                      <Check size={14} /> Marquer traité
                    </button>
                  )}
                  <button onClick={() => remove(openMsg)} data-testid="contact-delete" className="hard-border px-4 py-2 ticker hover:bg-[#D90429] hover:text-white hover:border-[#D90429] flex items-center gap-2 ml-auto">
                    <Trash size={14} /> Supprimer
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
